import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Users,
  BarChart3,
  TrendingUp,
  TrendingDown,
  Minus,
  RefreshCw,
  Loader2,
  CheckCircle,
  Target,
  ArrowUpRight,
  ArrowDownRight,
  Scale,
  Layers
} from 'lucide-react';
export default function AdminCohortComparison() {
  const [loading, setLoading] = useState(true);
  const [cohorts, setCohorts] = useState([]);
  const [benchmarks, setBenchmarks] = useState({});
  const [allCohorts, setAllCohorts] = useState([]);
  const [selectedCohorts, setSelectedCohorts] = useState([]);

  useEffect(() => {
    fetchAllCohorts();
    fetchComparison();
  }, []);

  const fetchAllCohorts = async () => {
    try {
      const response = await axios.get('/api/admin/cohorts');
      setAllCohorts(response.data.cohorts || []);
    } catch (error) {
      console.error('Failed to fetch cohorts:', error);
    }
  };

  const fetchComparison = async (cohortIds = null) => {
    try {
      setLoading(true);
      const params = cohortIds ? { cohort_ids: cohortIds.join(',') } : {};
      const response = await axios.get('/api/admin/analytics/cohort-comparison', { params });
      setCohorts(response.data.cohorts || []);
      setBenchmarks(response.data.benchmarks || {});
    } catch (error) {
      toast.error('Failed to load cohort comparison');
    } finally {
      setLoading(false);
    }
  };

  const handleCohortSelect = (cohortId) => {
    let newSelected;
    if (selectedCohorts.includes(cohortId)) {
      newSelected = selectedCohorts.filter(id => id !== cohortId);
    } else {
      newSelected = [...selectedCohorts, cohortId];
    }
    setSelectedCohorts(newSelected);
    
    if (newSelected.length > 0) {
      fetchComparison(newSelected);
    } else {
      fetchComparison();
    }
  };

  const ComparisonIndicator = ({ value, benchmark, suffix = '%', higherIsBetter = true }) => {
    if (!benchmark) return <span className="text-muted-foreground">-</span>;
    
    const diff = value - benchmark;
    const isGood = higherIsBetter ? diff >= 0 : diff <= 0;
    
    if (Math.abs(diff) < 1) {
      return (
        <span className="flex items-center gap-1 text-muted-foreground text-xs">
          <Minus className="w-3 h-3" />
          Avg
        </span>
      );
    }
    
    return (
      <span className={`flex items-center gap-1 text-xs ${isGood ? 'text-emerald-600' : 'text-red-500'}`}>
        {diff > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
        {diff > 0 ? '+' : ''}{diff.toFixed(1)}{suffix}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div data-testid="admin-cohort-comparison">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <Scale className="w-6 h-6 text-primary" />
              Cohort Comparison
            </h1>
            <p className="text-muted-foreground">
              Compare performance metrics across cohorts
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => fetchComparison(selectedCohorts.length > 0 ? selectedCohorts : null)} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Cohort Filter */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Layers className="w-5 h-5" />
              Select Cohorts to Compare
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {allCohorts.map(cohort => (
                <Button
                  key={cohort.id}
                  variant={selectedCohorts.includes(cohort.id) ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleCohortSelect(cohort.id)}
                >
                  {cohort.name}
                  {selectedCohorts.includes(cohort.id) && (
                    <CheckCircle className="w-3 h-3 ml-1" />
                  )}
                </Button>
              ))}
              {selectedCohorts.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedCohorts([]);
                    fetchComparison();
                  }}
                >
                  Clear Selection
                </Button>
              )}
            </div>
            {selectedCohorts.length === 0 && (
              <p className="text-sm text-muted-foreground mt-2">
                Showing all active cohorts. Click to select specific cohorts for comparison.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Benchmarks Summary */}
        {Object.keys(benchmarks).length > 0 && (
          <div className="grid md:grid-cols-5 gap-4 mb-6">
            <Card>
              <CardContent className="pt-4">
                <p className="text-xs text-muted-foreground mb-1">Avg EDGE Completion</p>
                <p className="text-2xl font-bold">{benchmarks.avgEdgeCompletionRate}%</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-xs text-muted-foreground mb-1">Avg CORE Completion</p>
                <p className="text-2xl font-bold">{benchmarks.avgCoreCompletionRate}%</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-xs text-muted-foreground mb-1">Avg Both Complete</p>
                <p className="text-2xl font-bold">{benchmarks.avgBothCompletionRate}%</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-xs text-muted-foreground mb-1">Avg Engagement</p>
                <p className="text-2xl font-bold">{benchmarks.avgEngagementRate}%</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-xs text-muted-foreground mb-1">Avg Acceptance</p>
                <p className="text-2xl font-bold">{benchmarks.avgAcceptanceRate}%</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Comparison Table */}
        <Card>
          <CardHeader>
            <CardTitle>Cohort Metrics</CardTitle>
            <CardDescription>
              Side-by-side performance comparison with benchmark indicators
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : cohorts.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No cohort data available</p>
              </div>
            ) : (
              <div className="overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="sticky left-0 bg-background">Cohort</TableHead>
                      <TableHead className="text-center">Users</TableHead>
                      <TableHead className="text-center">EDGE %</TableHead>
                      <TableHead className="text-center">CORE %</TableHead>
                      <TableHead className="text-center">Both %</TableHead>
                      <TableHead className="text-center">Engagement</TableHead>
                      <TableHead className="text-center">Sessions</TableHead>
                      <TableHead className="text-center">Applications</TableHead>
                      <TableHead className="text-center">Accepted</TableHead>
                      <TableHead className="text-center">Avg EDGE</TableHead>
                      <TableHead className="text-center">Avg CORE</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cohorts.map((cohort) => (
                      <TableRow key={cohort.cohortId}>
                        <TableCell className="sticky left-0 bg-background font-medium">
                          <div>
                            <p>{cohort.cohortName}</p>
                            <p className="text-xs text-muted-foreground">
                              {formatDate(cohort.startDate)}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline">{cohort.totalUsers}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex flex-col items-center gap-1">
                            <span className="font-medium">{cohort.edgeCompletionRate}%</span>
                            <ComparisonIndicator 
                              value={cohort.edgeCompletionRate} 
                              benchmark={benchmarks.avgEdgeCompletionRate} 
                            />
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex flex-col items-center gap-1">
                            <span className="font-medium">{cohort.coreCompletionRate}%</span>
                            <ComparisonIndicator 
                              value={cohort.coreCompletionRate} 
                              benchmark={benchmarks.avgCoreCompletionRate} 
                            />
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex flex-col items-center gap-1">
                            <span className="font-medium">{cohort.bothCompletionRate}%</span>
                            <ComparisonIndicator 
                              value={cohort.bothCompletionRate} 
                              benchmark={benchmarks.avgBothCompletionRate} 
                            />
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex flex-col items-center gap-1">
                            <span className="font-medium">{cohort.engagementRate}%</span>
                            <ComparisonIndicator 
                              value={cohort.engagementRate} 
                              benchmark={benchmarks.avgEngagementRate} 
                            />
                          </div>
                        </TableCell>
                        <TableCell className="text-center">{cohort.sessionsBooked}</TableCell>
                        <TableCell className="text-center">{cohort.applicationsSubmitted}</TableCell>
                        <TableCell className="text-center">
                          <div className="flex flex-col items-center gap-1">
                            <span className="font-medium">{cohort.applicationsAccepted}</span>
                            <Badge 
                              variant="outline" 
                              className={cohort.acceptanceRate > benchmarks.avgAcceptanceRate ? 'border-emerald-500 text-emerald-600' : ''}
                            >
                              {cohort.acceptanceRate}%
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          {cohort.avgEdgeScore > 0 ? cohort.avgEdgeScore : '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          {cohort.avgCoreScore > 0 ? cohort.avgCoreScore : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Visual Comparison Charts - Progress Bars */}
        {cohorts.length > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Visual Comparison
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* EDGE Completion */}
              <div>
                <h4 className="font-medium mb-3">EDGE Completion Rate</h4>
                <div className="space-y-3">
                  {cohorts.map(cohort => (
                    <div key={`edge-${cohort.cohortId}`} className="flex items-center gap-4">
                      <span className="w-32 text-sm truncate">{cohort.cohortName}</span>
                      <div className="flex-1">
                        <Progress value={cohort.edgeCompletionRate} className="h-4" />
                      </div>
                      <span className="w-16 text-sm font-medium text-right">
                        {cohort.edgeCompletionRate}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CORE Completion */}
              <div>
                <h4 className="font-medium mb-3">CORE Completion Rate</h4>
                <div className="space-y-3">
                  {cohorts.map(cohort => (
                    <div key={`core-${cohort.cohortId}`} className="flex items-center gap-4">
                      <span className="w-32 text-sm truncate">{cohort.cohortName}</span>
                      <div className="flex-1">
                        <Progress value={cohort.coreCompletionRate} className="h-4" />
                      </div>
                      <span className="w-16 text-sm font-medium text-right">
                        {cohort.coreCompletionRate}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Engagement Rate */}
              <div>
                <h4 className="font-medium mb-3">Engagement Rate</h4>
                <div className="space-y-3">
                  {cohorts.map(cohort => (
                    <div key={`eng-${cohort.cohortId}`} className="flex items-center gap-4">
                      <span className="w-32 text-sm truncate">{cohort.cohortName}</span>
                      <div className="flex-1">
                        <Progress value={cohort.engagementRate} className="h-4" />
                      </div>
                      <span className="w-16 text-sm font-medium text-right">
                        {cohort.engagementRate}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
  );
}
